import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Container, Alert, Table, Spinner, Row, Col, Form } from 'react-bootstrap'
import { fetchActions, updateActionStatus, ActionListDto } from '../services/api'

const ACTION_STATUSES = ['Open', 'InProgress', 'Resolved', 'Closed'] as const

export default function Actions() {
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const checkRunIdFromUrl = searchParams.get('checkRunId')
  const { data, isLoading, isError } = useQuery<ActionListDto[]>({
    queryKey: ['actions', checkRunIdFromUrl],
    queryFn: () => fetchActions({ checkRunId: checkRunIdFromUrl ?? undefined }),
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, newStatus }: { id: string; newStatus: string }) => updateActionStatus(id, newStatus),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['actions'] }),
  })
  const [status, setStatus] = React.useState('All')
  const [priority, setPriority] = React.useState('All')

  const filtered = React.useMemo<ActionListDto[]>(() => {
    if (!data) return []
    return data.filter((a) => {
      const matchesStatus = status === 'All' || a.status === status
      const matchesPriority = priority === 'All' || a.priority === priority
      return matchesStatus && matchesPriority
    })
  }, [data, status, priority])

  const statusOptions = React.useMemo(() => {
    const unique = new Set<string>()
    data?.forEach((a) => unique.add(a.status))
    return ['All', ...Array.from(unique)]
  }, [data])

  const priorityOptions = React.useMemo(() => {
    const unique = new Set<string>()
    data?.forEach((a) => {
      if (a.priority) unique.add(a.priority)
    })
    return ['All', ...Array.from(unique)]
  }, [data])

  return (
    <Container>
      <h1 className="mb-4">Actions</h1>
      <Alert variant="info">
        <strong>Actions</strong> are remediation tasks created from findings. Update status below to track progress.
        Use Distributor Action Reports to export for follow-up.
      </Alert>

      {isLoading && (
        <Alert variant="secondary" className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" /> Loading actions...
        </Alert>
      )}
      {isError && <Alert variant="danger">Failed to load actions.</Alert>}

      {data && (
        <>
          <Row className="mb-3">
            <Col md={3} className="mb-2">
              <Form.Select value={status} onChange={e => setStatus(e.target.value)}>
                {statusOptions.map(option => (
                  <option key={option} value={option}>
                    {option === 'All' ? 'All statuses' : option}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="mb-2">
              <Form.Select value={priority} onChange={e => setPriority(e.target.value)}>
                {priorityOptions.map(option => (
                  <option key={option} value={option}>
                    {option === 'All' ? 'All priorities' : option}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {filtered.length === 0 ? (
            <Alert variant="success">No actions match the selected filters.</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Due</th>
                  <th>Assigned To</th>
                  <th>Description</th>
                  <th>Finding</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={a.status || 'Open'}
                        style={{ minWidth: 130 }}
                        disabled={updateMutation.isPending}
                        onChange={(e) => {
                          const newStatus = e.target.value
                          if (newStatus && newStatus !== a.status) {
                            updateMutation.mutate({ id: a.id, newStatus })
                          }
                        }}
                      >
                        {ACTION_STATUSES.map((s) => (
                          <option key={s} value={s}>{s === 'InProgress' ? 'In progress' : s}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      {a.priority ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: 'white',
                          backgroundColor: getPriorityColor(a.priority)
                        }}>{a.priority}</span>
                      ) : '-'}
                    </td>
                    <td className={isOverdue(a.dueDate, a.status) ? 'text-danger' : undefined}>
                      {a.dueDate ? new Date(a.dueDate).toLocaleDateString() : '-'}
                    </td>
                    <td>{a.assignedTo || '-'}</td>
                    <td>{a.description || '-'}</td>
                    <td>{a.findingId}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Container>
  )
}

function getPriorityColor(priority: string) {
  switch (priority) {
    case 'High':
      return '#dc3545'
    case 'Medium':
      return '#fd7e14'
    case 'Low':
      return '#6c757d'
    default:
      return '#adb5bd'
  }
}

function isOverdue(dueDate?: string, status?: string) {
  if (!dueDate) return false
  const isClosed = status === 'Resolved' || status === 'Closed'
  const due = new Date(dueDate)
  const today = new Date()
  return !isClosed && due.getTime() < today.getTime()
}
